/**
 * LeadForge France - Global Configuration Context
 * All configuration values fetched from backend
 */
import React, { createContext, useContext, useState, useEffect } from 'react';
import { configAPI } from './api';

const ConfigContext = createContext(null);

export const useConfig = () => {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error('useConfig must be used within ConfigProvider');
  }
  return context;
};

// Default config (fallback)
const defaultConfig = {
  app_name: 'LeadForge France',
  app_tagline: 'La base de données B2B officielle de France',
  app_domain: 'leadforgefrance.com',
  contact_email: 'contact@leadforgefrance.com',
  contact_phone: '',
  support_hours: 'Lundi – Vendredi 9h00 – 18h00',
  default_country: 'France',
  currency: 'EUR',
  currency_symbol: '€',
  data_source: 'INSEE SIRENE',
  rgpd_notice: 'Données professionnelles issues de sources publiques.',
  ga4_measurement_id: '',
  features: {
    subscriptions_enabled: false,
    api_access_enabled: false,
    multi_country_enabled: false,
    crm_integration_enabled: false,
  },
  volumes: [100, 500, 1000, 5000, 10000],
  export_formats: ['csv', 'xlsx', 'json'],
};

export const ConfigProvider = ({ children }) => {
  const [config, setConfig] = useState(defaultConfig);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const response = await configAPI.get();
        setConfig({ ...defaultConfig, ...response.data });
      } catch (err) {
        console.error('Error loading config:', err);
        setError(err);
        // Use default config on error
      } finally {
        setLoading(false);
      }
    };

    loadConfig();
  }, []);

  // Helper functions
  const hasPhone = config.contact_phone && config.contact_phone.length > 0;
  const isFeatureEnabled = (feature) => config.features?.[feature] || false;

  return (
    <ConfigContext.Provider
      value={{
        config,
        loading,
        error,
        hasPhone,
        isFeatureEnabled,
      }}
    >
      {children}
    </ConfigContext.Provider>
  );
};

export default ConfigContext;
